import { DEFAULT_FEATURE_FLAGS } from '@volley/crucible-ipc-types';

const LOCAL_CLOUD_MOCK_FLAG = 'VITE_FOUNDRY_LOCAL_CLOUD_MOCK';
const LOCAL_CLOUD_MOCK_USER_ID = 'local-cloud-dev-user';

const SUPPORTED_METHOD_VERSIONS: Record<string, number[]> = {
  'app.get-feature-flags': [1],
  'app.get-furnace-status': [1],
  'agent.send': [1],
  'chat.load': [1],
  'chat.save': [1],
  'game.create': [1],
  'game.list': [1],
  'game.status': [1],
  'game.suggest-name': [1],
  'github.get-user': [1],
  'session.hello': [1],
  'system.info': [1],
};

// Spread the canonical default + override only what the local-cloud
// mock pins (#2197): cloudVmV1 ON (this mock simulates the cloud-VM
// runtime), lazyDashboardLoad + designTab OFF.
const FEATURE_FLAGS_SNAPSHOT = {
  ...DEFAULT_FEATURE_FLAGS,
  lazyDashboardLoad: false,
  cloudVmV1: true,
  designTab: false,
};

const MY_GAMES = [
  {
    gameId: 'headline-chronology',
    name: 'Headline Chronology',
    slug: 'headline-chronology',
    localPath: '',
    lastModified: '2026-06-16T16:00:00.000Z',
    status: 'draft',
    description: 'Sort news moments into the right timeline.',
    heroImage: null,
  },
  {
    gameId: 'headline-hustle',
    name: 'Headline Hustle',
    slug: 'headline-hustle',
    localPath: '',
    lastModified: '2026-06-15T16:00:00.000Z',
    status: 'draft',
    description: 'Race the clock to match headlines with clues.',
    heroImage: null,
  },
  {
    gameId: 'headline-jump',
    name: 'Headline Jump',
    slug: 'headline-jump',
    localPath: '',
    lastModified: '2026-06-14T16:00:00.000Z',
    status: 'draft',
    description: 'Leap between categories and current-events trivia.',
    heroImage: null,
  },
] as const;

interface JsonRpcRequest {
  readonly id?: string | number | null;
  readonly method?: string;
  readonly params?: unknown;
}

interface JsonRpcResponseBody {
  readonly result?: unknown;
  readonly error?: { readonly code: number; readonly message: string };
}

declare global {
  interface Window {
    __foundryLocalCloudMockInstalled?: boolean;
    __foundryLocalCloudMockMethods?: string[];
    __restoreFoundryLocalCloudMock?: () => void;
  }
}

function shouldInstallLocalCloudMock(): boolean {
  const env =
    (
      import.meta as unknown as {
        env?: Record<string, string | boolean | undefined>;
      }
    ).env ?? {};
  return env.DEV === true && env[LOCAL_CLOUD_MOCK_FLAG] === '1';
}

function requestPath(input: RequestInfo | URL): string | null {
  const raw =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : 'url' in input
          ? input.url
          : '';
  try {
    return new URL(raw, window.location.href).pathname;
  } catch {
    return null;
  }
}

function jsonResponse(body: unknown, init: ResponseInit = {}): Response {
  return new Response(JSON.stringify(body), {
    status: 200,
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
}

function emitMessage(
  target: EventTarget & { onmessage: ((event: MessageEvent) => void) | null },
  data: unknown,
): void {
  const event = new MessageEvent('message', {
    data: JSON.stringify(data),
  });
  target.onmessage?.(event);
  target.dispatchEvent(event);
}

function emitClose(
  target: EventTarget & { onclose: ((event: CloseEvent) => void) | null; readyState: number },
  code: number,
  reason: string,
): void {
  target.readyState = WebSocket.CLOSED;
  const event = new CloseEvent('close', {
    code,
    reason,
    wasClean: code === 1000,
  });
  target.onclose?.(event);
  target.dispatchEvent(event);
}

function responseForMethod(method: string): JsonRpcResponseBody | null {
  if (method === 'session.hello@1') {
    return {
      result: {
        sessionId: `local-cloud-${Math.random().toString(36).slice(2)}`,
        supportedMethodVersions: SUPPORTED_METHOD_VERSIONS,
        guiCapabilities: {
          hasRuntimePreview: true,
          canPublishRun: true,
          ingotScaffolderAvailable: true,
        },
      },
    };
  }
  if (method === 'system.info@1') {
    return {
      result: {
        runtimeVersion: 'local-cloud-dev-mock',
        supportedMethodVersions: SUPPORTED_METHOD_VERSIONS,
        guiCapabilities: {
          hasRuntimePreview: true,
          canPublishRun: true,
          ingotScaffolderAvailable: true,
        },
      },
    };
  }
  if (method === 'app.get-feature-flags@1') {
    return { result: FEATURE_FLAGS_SNAPSHOT };
  }
  if (method === 'app.get-furnace-status@1') {
    return {
      result: {
        cloudVmV1Enabled: true,
        cohort: 'local-dev',
        userId: LOCAL_CLOUD_MOCK_USER_ID,
        lastBuildTarget: null,
      },
    };
  }
  if (method === 'game.list@1') {
    return { result: MY_GAMES };
  }
  if (method === 'game.status@1') {
    return {
      result: {
        ok: true,
        status: 'draft',
        deployedUrl: null,
        lastBuildAt: null,
      },
    };
  }
  if (method === 'game.suggest-name@1') {
    return { result: { displayName: 'Local Cloud Game' } };
  }
  if (method === 'chat.load@1') {
    return { result: [] };
  }
  if (method === 'chat.save@1') {
    return { result: {} };
  }
  if (method === 'github.get-user@1') {
    return { result: null };
  }
  return {
    error: {
      code: -32601,
      message: `local cloud mock has no handler for ${method}`,
    },
  };
}

class LocalCloudMockWebSocket extends EventTarget {
  static readonly CONNECTING = WebSocket.CONNECTING;
  static readonly OPEN = WebSocket.OPEN;
  static readonly CLOSING = WebSocket.CLOSING;
  static readonly CLOSED = WebSocket.CLOSED;

  readonly url: string;
  readonly protocol: string;
  readonly extensions = '';
  readonly bufferedAmount = 0;
  binaryType: BinaryType = 'blob';
  onopen: ((event: Event) => void) | null = null;
  onmessage: ((event: MessageEvent) => void) | null = null;
  onclose: ((event: CloseEvent) => void) | null = null;
  onerror: ((event: Event) => void) | null = null;
  readyState: number = WebSocket.CONNECTING;

  constructor(url: string, protocols?: string | string[]) {
    super();
    this.url = url;
    this.protocol = Array.isArray(protocols) ? (protocols[0] ?? '') : (protocols ?? '');
    window.setTimeout(() => {
      if (this.readyState !== WebSocket.CONNECTING) return;
      this.readyState = WebSocket.OPEN;
      const event = new Event('open');
      this.onopen?.(event);
      this.dispatchEvent(event);
    }, 0);
  }

  send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
    if (this.readyState !== WebSocket.OPEN || typeof data !== 'string') return;
    let request: JsonRpcRequest;
    try {
      request = JSON.parse(data) as JsonRpcRequest;
    } catch {
      return;
    }
    if (!request.method) return;
    window.__foundryLocalCloudMockMethods?.push(request.method);
    if (request.id === undefined || request.id === null) return;

    if (request.method === 'game.create@1') {
      this.handleGameCreate(request.id);
      return;
    }

    const body = responseForMethod(request.method);
    window.setTimeout(() => {
      emitMessage(this, {
        jsonrpc: '2.0',
        id: request.id,
        ...body,
      });
    }, 0);
  }

  close(code = 1000, reason = ''): void {
    if (this.readyState === WebSocket.CLOSED) return;
    emitClose(this, code, reason);
  }

  private handleGameCreate(requestId: string | number): void {
    window.setTimeout(() => {
      emitMessage(this, {
        jsonrpc: '2.0',
        method: 'game.create.chunk@1',
        params: {
          requestId,
          seq: 0,
          chunkKind: 'progress',
          phase: 'scaffold',
          percent: 0,
          message: 'Creating local cloud game',
        },
      });
      emitMessage(this, {
        jsonrpc: '2.0',
        method: 'game.create.chunk@1',
        params: {
          requestId,
          seq: 1,
          chunkKind: 'progress',
          phase: 'done',
          percent: 100,
          message: 'Created local-cloud-game',
        },
      });
    }, 80);
    window.setTimeout(() => {
      emitMessage(this, {
        jsonrpc: '2.0',
        id: requestId,
        result: {
          gameId: 'local-cloud-game',
          slug: 'local-cloud-game',
          localPath: '',
          repoUrl: 'https://github.com/Volley-Inc/crucible-game-local-cloud-game',
        },
      });
    }, 250);
  }
}

export function maybeInstallLocalCloudMock(): void {
  if (!shouldInstallLocalCloudMock()) return;
  if (window.__foundryLocalCloudMockInstalled) return;

  const originalFetch = window.fetch.bind(window);
  const originalWebSocket = window.WebSocket;

  window.__foundryLocalCloudMockInstalled = true;
  window.__foundryLocalCloudMockMethods = [];
  window.__restoreFoundryLocalCloudMock = () => {
    window.fetch = originalFetch;
    window.WebSocket = originalWebSocket;
    window.__foundryLocalCloudMockInstalled = false;
    window.__foundryLocalCloudMockMethods = [];
  };

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const path = requestPath(input);
    if (path === '/crucible/me') {
      return jsonResponse({
        userId: LOCAL_CLOUD_MOCK_USER_ID,
        authMode: 'stub',
      });
    }
    if (path === '/crucible/version') {
      return jsonResponse({
        runtimeVersion: 'local-cloud-dev-mock',
        mode: 'stub',
      });
    }
    return originalFetch(input, init);
  };

  window.WebSocket = LocalCloudMockWebSocket as unknown as typeof WebSocket;
}
